import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Mail, 
  Globe, 
  Users, 
  Radio, 
  Smartphone,
  Wifi,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import ApproveApplicationModal from "../../../components/Affiliates/ApproveApplicationModal";
import RejectApplicationModal from "../../../components/Affiliates/RejectApplicationModal";
import PageTransition from "../../../components/PageTransition";

const AffiliateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState("properties");
  const [selectedProperty, setSelectedProperty] = useState("website");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Get application data from route state
  const application = location.state?.application;
  const initialTab = location.state?.activeTab || "properties";
  
  // Set initial tab from state
  useEffect(() => {
    if (initialTab) {
      setSelectedTab(initialTab);
    }
  }, [initialTab]);

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleApproveSuccess = () => {
    setShowApproveModal(false);
    // Navigate back to applications list after successful approval
    navigate("/affiliate-applications");
  };

  const handleRejectSuccess = () => {
    setShowRejectModal(false);
    // Navigate back to applications list after successful rejection
    navigate("/affiliate-applications");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return "⏳";
      case "approved":
        return "✅";
      case "rejected":
        return "❌";
      default:
        return "❓";
    }
  };

  const propertyTypes = [
    { id: "website", name: "Website", icon: Globe },
    { id: "social", name: "Social Media pages", icon: Users },
    { id: "mobile", name: "Mobile App", icon: Smartphone },
    { id: "podcast", name: "Podcast", icon: Radio },
    { id: "email", name: "Email/Newsletter", icon: Mail },
    { id: "offline", name: "Offline & other media", icon: Wifi },
  ];

  if (!application) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mb-4 text-6xl">😞</div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            No Application Data
          </h2>
          <p className="mb-4 text-gray-600">
            Please navigate from the applications table to view affiliate
            details.
          </p>
          <button
            onClick={() => navigate("/affiliate-applications")}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  const affiliate = application.affiliate;

  return (
    <>
      <Navbar page="Affiliate Details" />
      <Helmet>
        <title>
          Affiliate Details - {affiliate.first_name || 'Unknown'} {affiliate.last_name || 'User'} -
          DealBoxx Vendor
        </title>
        <meta
          name="description"
          content={`View detailed information about affiliate ${affiliate.first_name || 'Unknown'} ${affiliate.last_name || 'User'}`}
        />
      </Helmet>

      <PageTransition className="min-h-screen bg-gray-50">
        <div className="flex flex-col px-2 py-4 mx-auto max-w-7xl min-h-screen sm:px-4 sm:py-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            className="flex-shrink-0 mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <motion.button
              onClick={() => navigate("/affiliate-applications")}
              className="flex items-center text-gray-600 transition-colors hover:text-gray-900"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Applications
            </motion.button>
          </motion.div>

          <div className="flex flex-col flex-1 gap-4 min-h-0 lg:flex-row lg:gap-6">
            {/* Mobile Left Panel - Shows above content on small screens */}
            <motion.div
              className="w-full lg:hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                {/* Profile Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col items-center text-center">
                    {/* Profile Picture */}
                    <div className="flex justify-center items-center mb-4 w-20 h-20 text-2xl font-bold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                      {affiliate.first_name?.charAt(0)}
                      {affiliate.last_name?.charAt(0)}
                    </div>
                    
                    {/* Name and ID */}
                    <h1 className="mb-1 text-xl font-bold text-gray-900">
                      {affiliate.first_name || 'Unknown'} {affiliate.last_name || 'User'}
                    </h1>
                    <p className="mb-2 text-sm text-gray-500">
                      ID: {affiliate.affiliate_code || 'N/A'}
                    </p>
                    
                    {/* Role/Description */}
                    <p className="mb-3 text-sm text-gray-600">
                      {affiliate.account_display_name || "Affiliate"} |{" "}
                      {affiliate.account_type || "Partner"}
                    </p>
                    
                    {/* Status */}
                    <div className="flex items-center mb-4">
                      <div className="mr-2 w-2 h-2 bg-red-500 rounded-full"></div>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          application.status || "pending"
                        )}`}
                      >
                        <span className="mr-1">
                          {getStatusIcon(application.status || "pending")}
                        </span>
                        {application.status
                          ? application.status.charAt(0).toUpperCase() +
                            application.status.slice(1)
                          : "Active"}
                      </span>
                    </div>
                    
                    {/* Action Buttons - Only show for pending applications */}
                    {application.status === 'pending' && (
                      <motion.div
                        className="flex space-x-3 w-full"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <motion.button
                          onClick={handleApprove}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          Accept
                        </motion.button>
                        <motion.button
                          onClick={handleReject}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          Reject
                        </motion.button>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Desktop Left Panel - Fixed Affiliate Profile */}
            <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">
              <div className="flex sticky top-0 flex-col h-screen max-h-screen bg-white rounded-xl border border-gray-200 shadow-sm">
                {/* Profile Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col items-center text-center">
                    {/* Profile Picture */}
                    <div className="flex justify-center items-center mb-4 w-20 h-20 text-2xl font-bold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                      {affiliate.first_name?.charAt(0)}
                      {affiliate.last_name?.charAt(0)}
                    </div>

                    {/* Name and ID */}
                    <h1 className="mb-1 text-xl font-bold text-gray-900">
                      {affiliate.first_name || 'Unknown'} {affiliate.last_name || 'User'}
                    </h1>
                    <p className="mb-2 text-sm text-gray-500">
                      ID: {affiliate.affiliate_code || 'N/A'}
                    </p>

                    {/* Role/Description */}
                    <p className="mb-3 text-sm text-gray-600">
                      {affiliate.account_display_name || "Affiliate"} |{" "}
                      {affiliate.account_type || "Partner"}
                    </p>

                    {/* Status */}
                    <div className="flex items-center mb-4">
                      <div className="mr-2 w-2 h-2 bg-red-500 rounded-full"></div>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          application.status || "pending"
                        )}`}
                      >
                        <span className="mr-1">
                          {getStatusIcon(application.status || "pending")}
                        </span>
                        {application.status
                          ? application.status.charAt(0).toUpperCase() +
                            application.status.slice(1)
                          : "Active"}
                      </span>
                    </div>

                    {/* Action Buttons - Only show for pending applications */}
                    {application.status === 'pending' && (
                      <motion.div
                        className="flex space-x-3 w-full"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <motion.button
                          onClick={handleApprove}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          Accept
                        </motion.button>
                        <motion.button
                          onClick={handleReject}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          Reject
                        </motion.button>
                      </motion.div>
                    )}
                  </div>
                    </div>
              </div>
            </div>

            {/* Right Panel - Scrollable Content */}
            <motion.div
              className="flex flex-col flex-1 w-full min-h-0 lg:w-auto"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <motion.div
                className="flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                {/* Tabs */}
                <div className="flex-shrink-0 border-b border-gray-200">
                  <nav className="flex overflow-x-auto px-4 space-x-2 sm:px-6 sm:space-x-4 lg:overflow-x-visible lg:justify-center">
                    <motion.button
                      onClick={() => setSelectedTab("properties")}
                      className={`py-3 px-2 border-b-2 font-medium text-xs whitespace-nowrap sm:py-4 sm:px-1 sm:text-sm ${
                        selectedTab === "properties"
                          ? "border-red-500 text-red-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Properties
                    </motion.button>
                    <motion.button
                      onClick={() => setSelectedTab("details")}
                      className={`py-3 px-2 border-b-2 font-medium text-xs whitespace-nowrap sm:py-4 sm:px-1 sm:text-sm ${
                        selectedTab === "details"
                          ? "border-red-500 text-red-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Details
                    </motion.button>
                    <motion.button
                      onClick={() => setSelectedTab("contact")}
                      className={`py-3 px-2 border-b-2 font-medium text-xs whitespace-nowrap sm:py-4 sm:px-1 sm:text-sm ${
                        selectedTab === "contact"
                          ? "border-red-500 text-red-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Contact
                    </motion.button>
                    <motion.button
                      onClick={() => setSelectedTab("address")}
                      className={`py-3 px-2 border-b-2 font-medium text-xs whitespace-nowrap sm:py-4 sm:px-1 sm:text-sm ${
                        selectedTab === "address"
                          ? "border-red-500 text-red-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Address
                    </motion.button>
                  </nav>
                </div>

                {/* Scrollable Tab Content */}
                <div className="overflow-y-auto flex-1 p-4 min-h-0 sm:p-6">
                  <AnimatePresence mode="wait">
                  {selectedTab === "properties" ? (
                      <motion.div
                        key="properties"
                        className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                      {/* Properties Navigation */}
                      <div className="lg:col-span-1">
                        <div className="space-y-1">
                          {propertyTypes.map((property) => {
                            const Icon = property.icon;
                            return (
                              <button
                                key={property.id}
                                  onClick={() =>
                                    setSelectedProperty(property.id)
                                  }
                                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  selectedProperty === property.id
                                    ? "bg-red-50 text-red-700"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                }`}
                              >
                                <Icon className="mr-3 w-4 h-4" />
                                {property.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Property Details */}
                      <div className="lg:col-span-2">
                        <div className="p-4 rounded-lg border border-gray-200">
                          <h3 className="mb-4 text-sm font-medium text-red-600">
                              {
                                propertyTypes.find(
                                  (p) => p.id === selectedProperty
                                )?.name
                              }{" "}
                              name & URL
                          </h3>
                          
                          {selectedProperty === "website" && (
                            <div className="space-y-3">
                              {affiliate.website?.map((site, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                  >
                                  <div>
                                      <p className="font-medium text-gray-900">
                                        {site.name}
                                      </p>
                                  </div>
                                  <button className="px-3 py-1 text-sm font-medium text-red-600 rounded-lg border border-red-300 transition-colors hover:bg-red-50">
                                    Visit Website
                                  </button>
                                </div>
                              )) || (
                                <div className="py-8 text-center text-gray-500">
                                  <Globe className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                                  <p>No websites available</p>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedProperty === "social" && (
                            <div className="space-y-3">
                              {affiliate.social?.map((social, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                  >
                                  <div>
                                      <p className="font-medium text-gray-900">
                                        {social.platform}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {social.followers} followers
                                      </p>
                                  </div>
                                  <button className="px-3 py-1 text-sm font-medium text-red-600 rounded-lg border border-red-300 transition-colors hover:bg-red-50">
                                    Visit Profile
                                  </button>
                                </div>
                              )) || (
                                <div className="py-8 text-center text-gray-500">
                                  <Users className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                                  <p>No social media profiles available</p>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedProperty === "mobile" && (
                            <div className="space-y-3">
                              {affiliate.mobile_app?.map((app, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                  >
                                  <div>
                                      <p className="font-medium text-gray-900">
                                        {app.name}
                                      </p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {app.stores?.map(
                                          (store, storeIndex) => (
                                            <span
                                              key={storeIndex}
                                              className="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
                                            >
                                          {store}
                                        </span>
                                          )
                                        )}
                                      </div>
                                  </div>
                                  <button className="px-3 py-1 text-sm font-medium text-red-600 rounded-lg border border-red-300 transition-colors hover:bg-red-50">
                                    View App
                                  </button>
                                </div>
                              )) || (
                                <div className="py-8 text-center text-gray-500">
                                  <Smartphone className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                                  <p>No mobile apps available</p>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedProperty === "podcast" && (
                            <div className="space-y-3">
                              {affiliate.podcast?.map((podcast, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                  >
                                  <div>
                                      <p className="font-medium text-gray-900">
                                        {podcast.name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {podcast.description}
                                      </p>
                                  </div>
                                  <button className="px-3 py-1 text-sm font-medium text-red-600 rounded-lg border border-red-300 transition-colors hover:bg-red-50">
                                    Listen
                                  </button>
                                </div>
                              )) || (
                                <div className="py-8 text-center text-gray-500">
                                  <Radio className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                                  <p>No podcasts available</p>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedProperty === "email" && (
                            <div className="space-y-3">
                                {affiliate.email_channels?.map(
                                  (channel, index) => (
                                    <div
                                      key={index}
                                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                                    >
                                  <div>
                                        <p className="font-medium text-gray-900">
                                          {channel.name}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                          {channel.subscribers} subscribers
                                        </p>
                                  </div>
                                  <button className="px-3 py-1 text-sm font-medium text-red-600 rounded-lg border border-red-300 transition-colors hover:bg-red-50">
                                    Subscribe
                                  </button>
                                </div>
                                  )
                                ) || (
                                <div className="py-8 text-center text-gray-500">
                                  <Mail className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                                  <p>No email channels available</p>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedProperty === "offline" && (
                            <div className="py-8 text-center text-gray-500">
                              <Wifi className="mx-auto mb-2 w-12 h-12 text-gray-300" />
                              <p>No offline media available</p>
                            </div>
                          )}
                          </div>
                        </div>
                      </motion.div>
                    ) : selectedTab === "details" ? (
                    <motion.div
                      key="details"
                      className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Basic Information */}
                        <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                          <div className="flex gap-3 items-center mb-6">
                            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              Basic Information
                            </h3>
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  First Name
                                </label>
                                <p className="font-medium text-gray-900">
                                  {affiliate.first_name}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Last Name
                                </label>
                                <p className="font-medium text-gray-900">
                                  {affiliate.last_name}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Email
                                </label>
                                <p className="text-gray-600">
                                  {affiliate.email}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Phone Number
                                </label>
                                <p className="text-gray-600">
                                  {affiliate.phone_number}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Account Type
                                </label>
                                <p className="font-medium text-gray-900 capitalize">
                                  {affiliate.account_type} Account
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Display Name
                                </label>
                                <p className="text-gray-600">
                                  {affiliate.account_display_name || "N/A"}
                                </p>
                        </div>
                      </div>
                    </div>
                        </div>
                      </motion.div>
                    ) : selectedTab === "contact" ? (
                    <motion.div
                      key="contact"
                      className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Primary Contact Information */}
                        <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                          <div className="flex gap-3 items-center mb-6">
                            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              Primary Contact Information
                            </h3>
                          </div>
                          
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                          <div className="space-y-4">
                            <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
                                <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                                  <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                            <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Email Address
                                  </p>
                                  <p className="text-gray-900">
                                    {affiliate.email}
                                  </p>
                                </div>
                                  </div>

                              <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
                                <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-full">
                                  <Smartphone className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Phone Number
                                  </p>
                                  <p className="text-gray-900">
                                    {affiliate.phone_number}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-4">
                              <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
                                <div className="flex justify-center items-center w-10 h-10 bg-orange-100 rounded-full">
                                  <Users className="w-5 h-5 text-orange-600" />
                                </div>
                                  <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Account Type
                                  </p>
                                  <p className="text-gray-900 capitalize">
                                    {affiliate.account_type}
                                  </p>
                                    </div>
                                  </div>

                              <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
                                <div className="flex justify-center items-center w-10 h-10 bg-purple-100 rounded-full">
                                  <Globe className="w-5 h-5 text-purple-600" />
                                  </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Display Name
                                  </p>
                                  <p className="text-gray-900">
                                    {affiliate.account_display_name || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Website Information */}
                        {affiliate.website?.length > 0 && (
                          <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100">
                            <div className="flex gap-3 items-center mb-6">
                              <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                              <h3 className="text-lg font-semibold text-gray-900">
                                Website Information
                              </h3>
                            </div>

                            <div className="space-y-3">
                              {affiliate.website.map((site, index) => (
                                <div
                                  key={index}
                                  className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-100"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                                      <Globe className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {site.name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {site.url}
                                      </p>
                                    </div>
                                  </div>
                                  <a
                                    href={site.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    Visit Site
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ) : selectedTab === "address" ? (
                    <motion.div
                      key="address"
                      className="space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Address Information */}
                        {affiliate.address ? (
                          <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex gap-3 items-center mb-6">
                              <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-indigo-600 rounded-full" />
                              <h3 className="text-lg font-semibold text-gray-900">
                                Address Information
                              </h3>
                            </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium text-gray-500">
                                  Street Address 1
                                </label>
                                  <p className="text-gray-900">
                                    {affiliate.address.street_address_1}
                                  </p>
                                </div>
                                {affiliate.address.street_address_2 && (
                                  <div>
                                    <label className="text-sm font-medium text-gray-500">
                                      Street Address 2
                                    </label>
                                    <p className="text-gray-900">
                                      {affiliate.address.street_address_2}
                            </p>
                          </div>
                        )}
                                <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    City
                                  </label>
                                  <p className="text-gray-900">
                                    {affiliate.address.city}
                                  </p>
                      </div>
                        </div>

                          <div className="space-y-4">
                            <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Region
                                  </label>
                                  <p className="text-gray-900">
                                    {affiliate.address.region}
                                  </p>
                            </div>
                            <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    ZIP Code
                                  </label>
                                  <p className="text-gray-900">
                                    {affiliate.address.zip_code}
                                  </p>
                            </div>
                            <div>
                                  <label className="text-sm font-medium text-gray-500">
                                    Country
                                  </label>
                                  <p className="text-gray-900 capitalize">
                                    {affiliate.address.country}
                                  </p>
                            </div>
                          </div>
                            </div>

                            {/* Full Address Display */}
                            <div className="p-4 mt-6 bg-gray-50 rounded-lg">
                              <label className="text-sm font-medium text-gray-500">
                                Complete Address
                              </label>
                              <p className="mt-1 text-gray-900">
                                {affiliate.address.street_address_1}
                                {affiliate.address.street_address_2 &&
                                  `, ${affiliate.address.street_address_2}`}
                                , {affiliate.address.city},{" "}
                                {affiliate.address.region}{" "}
                                {affiliate.address.zip_code},{" "}
                                {affiliate.address.country}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                            <div className="text-center">
                              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-200 rounded-full">
                                <Globe className="w-8 h-8 text-gray-400" />
                        </div>
                              <h3 className="mb-2 text-lg font-medium text-gray-900">
                                No Address Information
                              </h3>
                              <p className="text-gray-500">
                                This affiliate has not provided address
                                information.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Additional Location Information */}
                        <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100">
                          <div className="flex gap-3 items-center mb-6">
                            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              Location Details
                            </h3>
                          </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                          <div className="flex items-center p-4 space-x-3 bg-white rounded-lg border border-gray-100">
                              <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                                <Globe className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                <p className="text-sm font-medium text-gray-500">
                                  Timezone
                                </p>
                                <p className="text-gray-900 capitalize">
                                  {affiliate.timezone?.replace("_", " ")}
                                </p>
                                  </div>
                                </div>

                            <div className="flex items-center p-4 space-x-3 bg-white rounded-lg border border-gray-100">
                              <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-full">
                                <span className="text-sm font-bold text-green-600">
                                  ₵
                                </span>
                                  </div>
                                  <div>
                                <p className="text-sm font-medium text-gray-500">
                                  Currency
                                </p>
                                <p className="text-gray-900 uppercase">
                                  {affiliate.currency}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </PageTransition>

      {/* Modals */}
      <AnimatePresence>
        {showApproveModal && application && (
          <ApproveApplicationModal
            application={application}
            onClose={() => setShowApproveModal(false)}
            onSuccess={handleApproveSuccess}
          />
        )}

        {showRejectModal && application && (
          <RejectApplicationModal
            application={application}
            onClose={() => setShowRejectModal(false)}
            onSuccess={handleRejectSuccess}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default AffiliateDetails;
